<template>
  <div class="bbp-form" @keydown.esc="closeForm" tabindex="0">
    <div class="bbp-form-header">
      <div class="bbp-form-header__title">{{ title }}</div>
      <i class="bbp-icon-cross bbp-form-header__close" @click="closeForm"/>
    </div>
    <VuePerfectScrollbar
      class="bbp-form-body"
      @ps-scroll-y="scrollHandle"
      :settings="{ wheelPropagation: false }"
    >
      <div class="bbp-form-body-item"
             v-for="(input, index) in inputs"
             v-if="!input.isHideRow"
             v-show="!hideInputs.includes(input.id)"
             :key="input.id"
             :style="input.grid || {}">
        <span
          v-if="input.label"
          class="bbp-form-body-item__label"
        >
          {{ input.label }}
        </span>
        <component :is="getComponentByType(input)"
                   :model="model"
                   :input="input"
                   :index="index"
                   :project="project"
                   :defaultModel="defaultModel"
                   :updateBody="updateBody"
                   :scrollTop="scrollTop"
                   :errorText="errors[input.model]"
                   @scrollEnd="scrollEnd(input)"
                   @error="setError($event, input)"
                   @input="setValue($event, input)"/>
      </div>
    </VuePerfectScrollbar>
    <div class="bbp-form-footer">
      <button class="bbp-button bbp-button_border bbp-button_border_gray" @click="closeForm">cancel</button>
      <button class="bbp-button" @click="saveForm" :disabled="hasErrors || disabledButton">
        {{ btnTextRight }}
      </button>
    </div>
  </div>
</template>

<script>
import {get, set, some, cloneDeep, isEqual} from 'lodash'
import BBPInputText from '@/components/Main/BBPForm/BBPInputText/BBPInputText'
import BBPTextarea from '@/components/Main/BBPForm/BBPTextarea/BBPTextarea'
import BBPTextareaEditor from '@/components/Main/BBPForm/BBPTextareaEditor/BBPTextareaEditor'
import BBPDatePicker from '@/components/Main/BBPForm/BBPDatePicker/BBPDatePicker'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import BBPSelectInput from '@/components/Main/BBPForm/BBPSelectInput/BBPSelectInput'
import BBPRadio from '@/components/Main/BBPForm/BBPRadio/BBPRadio'
import BBPAccordion from '@/components/Main/BBPForm/BBPAccordion/BBPAccordion'
import BBPCheckboxForm from '@/components/Main/BBPForm/BBPCheckboxForm/BBPCheckboxForm'
import BBPUserContainer from '@/components/Main/BBPForm/BBPUserContainer/BBPUserContainer'
import BBPNumberSelect from '@/components/Main/BBPForm/BBPNumberSelect/BBPNumberSelect'
import BBPFilesDrop from '@/components/Main/BBPForm/BBPFilesDrop/BBPFilesDrop'
import BBPSelectTagInput from '@/components/Main/BBPForm/BBPSelectTagInput/BBPSelectTagInput'
import BBPSelectCompany from '@/components/Main/BBPForm/BBPSelectCompany/BBPSelectCompany'
import BBPTimerManual from '@/components/Main/BBPForm/BBPTimerManual/BBPTimerManual'

export default {
  name: 'BBPForm',
  props: {
    disabledButton: Boolean,
    title: {
      type: String,
      default: ''
    },
    inputs: {
      type: Array,
      default: () => []
    },
    defaultModel: {
      type: Object,
      default: () => {
        return {}
      }
    },
    updateBody: {
      type: Object,
      default: () => ({})
    },
    project: {
      type: Object,
      default: () => {
        return {}
      }
    },
    btnTextRight: {
      type: String,
      default: 'ADD'
    }
  },
  components: {VuePerfectScrollbar},
  data () {
    return {
      model: {},
      errors: {},
      scrollTop: 0
    }
  },
  computed: {
    hasErrors () {
      return some(this.errors, (item) => item)
    },
    hideInputs () {
      return this.inputs.map(input => {
        if (!input.show) return
        const needShow = get(this.model, input.show, false)
        return needShow ? '' : input.id
      })
    }
  },
  methods: {
    scrollHandle (evt) {
      const { srcElement } = evt
      this.scrollTop = srcElement.scrollTop
    },
    setValue (value, input) {
      this.$set(this.model, input.model, value)
      if (input.resetErrors) this.errors = {}
      this.$emit('update', {model: this.model, input: input, value: value})
    },
    setError (value, input) {
      set(this.errors, input.model, value)
      this.errors = cloneDeep(this.errors)
    },
    checkShow (input) {
      return input.show ? get(this.model, input.model, false) : true
    },
    scrollEnd (input) {
      this.$emit('scrollEnd', input)
    },
    getComponentByType (input) {
      const type = get(input, 'type', '')
      switch (type) {
      case 'select':
        return BBPSelectInput
      case 'textarea':
        return BBPTextarea
      case 'textareaEditor':
        return BBPTextareaEditor
      case 'date':
        return BBPDatePicker
      case 'radio':
        return BBPRadio
      case 'numberSelect':
        return BBPNumberSelect
      case 'files':
        return BBPFilesDrop
      case 'accordion':
        return BBPAccordion
      case 'checkbox':
        return BBPCheckboxForm
      case 'userContainer':
        return BBPUserContainer
      case 'selectTag':
        return BBPSelectTagInput
      case 'selectCompany':
        return BBPSelectCompany
      case 'timerManual':
        return BBPTimerManual
      default:
        return BBPInputText
      }
    },
    closeForm () {
      this.$emit('close')
    },
    saveForm () {
      this.checkedRequired()
      if (some(this.errors, item => item)) return
      this.$emit('save', this.model)
    },
    checkedRequired () {
      const requiredInputs = this.inputs.filter(input => input.required)
      requiredInputs.forEach(input => {
        const model = get(this.model, input.model, '')
        let errorValue
        if (input.model === 'companyIsTab') {
          const { companiesSelect } = this.project
          errorValue = !(model && companiesSelect.length)
        } else {
          const hasGetValue = get(input, 'getValue', false)
          const value = hasGetValue ? hasGetValue(model) : model
          errorValue = value || (value === 0 && !input.zeroIsNotValid) ? false : 'mandatory field'
          if (input.propRequired) {
            this.check(input)
            return
          }
        }
        this.setError(errorValue, input)
      })
      const notMoreInput = this.inputs.filter(input => input.notMore)
      notMoreInput.forEach(input => {
        const factValue = get(this.model, input.model, 0)
        const currentValue = get(this.model, input.notMore, 0)
        const errorValue = factValue > currentValue
          ? `The fact should not be more than AED ${currentValue}`
          : false
        this.setError(errorValue, input)
      })
    },
    check (input) {
      const valueInputs = get(this.model, input.model, [])
      const result = valueInputs.some(item => !item.percent)
      const textError = 'enter the number'
      this.setError(result ? textError : false, input)
    },
    setDefaultModel () {
      const newModel = {}
      const id = get(this.defaultModel, 'id', '')
      if (id) set(newModel, 'id', id)
      this.inputs.forEach(input => {
        const value = get(this.defaultModel, input.model, null)
        if (value) {
          set(newModel, input.model, value)
        }
        const notMore = get(this.defaultModel, input.notMore, null)
        if (notMore) {
          set(newModel, input.notMore, notMore)
        }
      })
      this.model = newModel
    }
  },
  watch: {
    defaultModel (newVal, oldVal) {
      if (isEqual(newVal, oldVal)) return
      this.setDefaultModel()
    }
  },
  beforeMount () {
    this.setDefaultModel()
  }
}
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bbp-form {
  @include global-font;
  display: grid;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 60px);
  background: $color-white;
  border-radius: 8px;

  &-header {
    padding: 15px 18px;
    position: relative;
    display: grid;
    grid-template-columns: 1fr 40px;
    align-items: center;
    justify-content: center;

    &__close {
      position: absolute;
      top: 11px;
      right: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $color-silver;
      width: 26px;
      height: 26px;
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        color: $color-dusty-gray
      }
    }

    &__title {
      font-weight: 500;
      font-size: 17px;
      text-align: center;
    }
  }

  &-body {
    display: grid;
    grid-gap: 22px;
    margin: 20px auto 36px;
    padding: 15px 18px;
    width: 100%;
    max-height: calc(90vh - 188px);
    overflow: hidden;
    transition: 0.3s;

    &-item {
      display: grid;
      grid-gap: 8px;
      font-size: 14px;
      line-height: 17px;

      &__label {
        display: flex;
        align-items: center;
        color: $color-accent;
        font-weight: 600;
        font-size: 13px;
        line-height: 16px;
      }
    }
  }

  &-footer {
    position: sticky;
    bottom: 0;
    display: grid;
    grid-template-columns: repeat(2, 103px);
    grid-gap: 12px;
    justify-content: end;
    padding: 18px;
    border-top: 1px solid $color-mercury;
    background: $color-alabaster;
    border-radius: 0 0 8px 8px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Main/BBPForm/BBPForm.vue